import React, {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { cx } from './hooks';

/*
 * Small form helper: values, validation and submit for plain inputs.
 *
 *   const form = useForm();
 *   <Form form={form} initialValues={{ name: '' }} onFinish={values => save(values)}>
 *     <Field name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
 *       <input className="input" />
 *     </Field>
 *     <Field name="phone" label="Phone">
 *       {field => <div className="input-affix"><AiOutlinePhone /><input {...field} /></div>}
 *     </Field>
 *     <button className="btn btn-primary" type="submit">Save</button>
 *   </Form>
 *
 * Rules: required, message, type ('email' | 'url' | 'number' | …),
 * min, max, len, pattern, whitespace, enum, validator(rule, value) => Promise,
 * or a function ({ getFieldValue }) => rule.
 *
 * form methods: getFieldValue, getFieldsValue, setFieldValue, setFieldsValue,
 * resetFields, validateFields, submit, setFields, getFieldError, values, errors.
 */

// ---------- name paths ----------
const toPath = name => (Array.isArray(name) ? name : [name]);
const toKey = name => toPath(name).join('.');

function getIn(obj, path) {
  return path.reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

function setIn(obj, path, value) {
  if (!path.length) return value;
  const [head, ...rest] = path;
  const base = obj ?? (typeof head === 'number' ? [] : {});
  const copy = Array.isArray(base) ? [...base] : { ...base };
  copy[head] = setIn(base[head], rest, value);
  return copy;
}

// ---------- validation ----------
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// An absolute http(s)/ftp address with a host.
const isUrl = value => {
  try {
    const url = new URL(value);
    return ['http:', 'https:', 'ftp:'].includes(url.protocol) && url.hostname.includes('.');
  } catch {
    return false;
  }
};

const isEmpty = value =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value === '') ||
  (Array.isArray(value) && value.length === 0);

function typeMismatch(type, value) {
  switch (type) {
    case 'email':
      return typeof value !== 'string' || !EMAIL_RE.test(value);
    case 'url':
      return typeof value !== 'string' || !isUrl(value);
    case 'number':
    case 'float':
      return typeof value !== 'number' || Number.isNaN(value);
    case 'integer':
      return !Number.isInteger(value);
    case 'boolean':
      return typeof value !== 'boolean';
    case 'array':
      return !Array.isArray(value);
    case 'string':
      return typeof value !== 'string';
    default:
      return false;
  }
}

function runValidator(rule, value) {
  return new Promise((resolve, reject) => {
    const callback = err => (err ? reject(err) : resolve());
    try {
      const result = rule.validator(rule, value, callback);
      if (result && typeof result.then === 'function') result.then(resolve, reject);
      else if (rule.validator.length < 3) resolve();
    } catch (err) {
      reject(err);
    }
  });
}

// Default messages for rules without their own `message`.
function sizeMessage(rule, size, kind, name) {
  const { len, min, max } = rule;
  const unit = kind === 'string' ? ' characters' : '';
  if (len !== undefined) {
    if (size === len) return null;
    if (kind === 'array') return `Must be ${len} ${name}`;
    return kind === 'number' ? `${name} must be equal to ${len}` : `${name} must be ${len}${unit}`;
  }
  const tooSmall = min !== undefined && size < min;
  const tooBig = max !== undefined && size > max;
  if (!tooSmall && !tooBig) return null;
  if (min !== undefined && max !== undefined) {
    return kind === 'array'
      ? `The amount of ${name} must be between ${min}-${max}`
      : `${name} must be between ${min}-${max}${unit}`;
  }
  if (tooSmall) {
    if (kind === 'array') return `At least ${min} ${name}`;
    return kind === 'number' ? `${name} must be minimum ${min}` : `${name} must be at least ${min}${unit}`;
  }
  if (kind === 'array') return `At most ${max} ${name}`;
  return kind === 'number' ? `${name} must be maximum ${max}` : `${name} must be up to ${max}${unit}`;
}

async function validateValue(value, rules, form, label, fieldName) {
  const name = typeof label === 'string' && label ? label : fieldName || 'This field';

  for (const raw of rules || []) {
    const rule = typeof raw === 'function' ? raw(form) : raw;
    if (!rule) continue;

    const blank = isEmpty(value);
    const onlySpaces = rule.whitespace && typeof value === 'string' && value !== '' && !value.trim();

    if (rule.required && blank) return rule.message || `Please enter ${name}`;
    if (onlySpaces) return rule.message || `${name} cannot be a blank character`;

    if (rule.validator) {
      try {
        await runValidator(rule, value);
      } catch (err) {
        if (typeof err === 'string') return err;
        return err?.message || rule.message || `Field validation error for ${name}`;
      }
      continue;
    }

    if (blank) continue;

    if (rule.type && typeMismatch(rule.type, value)) {
      return rule.message || `${name} is not a valid ${rule.type}`;
    }

    if (rule.pattern) {
      rule.pattern.lastIndex = 0;
      if (!rule.pattern.test(String(value))) {
        return rule.message || `${name} does not match the pattern ${rule.pattern}`;
      }
    }

    if (rule.len !== undefined || rule.min !== undefined || rule.max !== undefined) {
      let kind = null;
      let size;
      if (typeof value === 'number') {
        kind = 'number';
        size = value;
      } else if (Array.isArray(value)) {
        kind = 'array';
        size = value.length;
      } else if (typeof value === 'string') {
        kind = 'string';
        size = [...value].length;
      }
      if (kind) {
        const message = sizeMessage(rule, size, kind, name);
        if (message) return rule.message || message;
      }
    }

    if (rule.enum && !rule.enum.includes(value)) {
      return rule.message || `${name} must be one of [${rule.enum.join(', ')}]`;
    }
  }
  return null;
}

// ---------- store ----------
function createFormStore() {
  let values = {};
  let initialValues = {};
  let errors = {};
  let version = 0;
  let initialized = false;
  const fields = new Map(); // key -> { path, meta: { current: { rules, label } }, count }
  const listeners = new Set();
  const validationRuns = {};
  const callbacks = { onFinish: null, onFinishFailed: null, onValuesChange: null };

  const notify = () => {
    version += 1;
    listeners.forEach(listener => listener());
  };

  const store = {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getVersion: () => version,

    get values() {
      return values;
    },
    get errors() {
      return errors;
    },

    getFieldValue: name => getIn(values, toPath(name)),
    getFieldError: name => errors[toKey(name)],
    getFieldsError: () => Object.entries(errors).map(([key, msg]) => ({ name: key.split('.'), errors: [msg] })),

    /** Values of the fields currently on screen (values without a Field are left out). */
    getFieldsValue(all) {
      if (all === true) return values;
      const result = {};
      fields.forEach(({ path }) => {
        const top = path[0];
        result[top] = values[top];
      });
      return result;
    },

    /** Set a value from code. This does not validate or fire onValuesChange. */
    setFieldValue(name, value) {
      values = setIn(values, toPath(name), value);
      notify();
    },

    setFieldsValue(partial) {
      values = { ...values, ...partial };
      notify();
    },

    /** Back to the initial values. Pass names to reset only those fields. */
    resetFields(names) {
      if (names?.length) {
        names.forEach(name => {
          const path = toPath(name);
          values = setIn(values, path, getIn(initialValues, path));
          delete errors[toKey(name)];
        });
        errors = { ...errors };
      } else {
        values = { ...initialValues };
        errors = {};
      }
      // Ignore validations that were still running for the old values.
      Object.keys(validationRuns).forEach(key => {
        validationRuns[key] += 1;
      });
      notify();
    },

    /** setFields([{ name, value?, errors?: [msg] }]) — handy for server-side errors. */
    setFields(list) {
      list.forEach(({ name, value, errors: errs }) => {
        if (value !== undefined) values = setIn(values, toPath(name), value);
        if (errs) {
          errors = { ...errors };
          if (errs.length) errors[toKey(name)] = errs[0];
          else delete errors[toKey(name)];
        }
      });
      notify();
    },

    async validateField(name) {
      const key = toKey(name);
      const entry = fields.get(key);
      if (!entry) return null;
      validationRuns[key] = (validationRuns[key] || 0) + 1;
      const run = validationRuns[key];
      const { rules, label } = entry.meta.current;
      const error = await validateValue(getIn(values, entry.path), rules, store, label, entry.path.join('.'));
      if (run !== validationRuns[key] || !fields.has(key)) return error;
      if ((errors[key] || null) !== error) {
        errors = { ...errors };
        if (error) errors[key] = error;
        else delete errors[key];
        notify();
      }
      return error;
    },

    /** Resolves with the values, or rejects with { errorFields, values }. */
    async validateFields(names) {
      const keys = names?.length ? names.map(toKey) : [...fields.keys()];
      const results = await Promise.all(keys.map(key => store.validateField(fields.get(key)?.path ?? key)));
      const errorFields = keys
        .map((key, i) => (results[i] ? { name: toPath(fields.get(key)?.path ?? key), errors: [results[i]] } : null))
        .filter(Boolean);
      const current = store.getFieldsValue();
      if (errorFields.length) {
        // eslint-disable-next-line no-throw-literal
        throw { errorFields, values: current };
      }
      return current;
    },

    async submit() {
      try {
        const result = await store.validateFields();
        await callbacks.onFinish?.(result);
      } catch (err) {
        if (err && err.errorFields) {
          callbacks.onFinishFailed?.(err);
          const first = err.errorFields[0];
          if (first) {
            const el = document.getElementById(fieldDomId(store, first.name));
            el?.focus?.();
          }
        } else {
          throw err;
        }
      }
    },

    // ----- internal -----
    /** A change made by the user through a Field: validates and fires onValuesChange. */
    _change(name, value) {
      const path = toPath(name);
      values = setIn(values, path, value);
      callbacks.onValuesChange?.(setIn({}, path, value), values);
      notify();
      if (fields.has(toKey(name))) store.validateField(name);
    },
    _register(name, meta) {
      const key = toKey(name);
      const existing = fields.get(key);
      if (existing) {
        existing.count += 1;
        existing.meta = meta;
      } else {
        fields.set(key, { path: toPath(name), meta, count: 1 });
      }
      return () => {
        const entry = fields.get(key);
        if (!entry) return;
        entry.count -= 1;
        if (entry.count <= 0) {
          fields.delete(key);
          if (errors[key]) {
            errors = { ...errors };
            delete errors[key];
            notify();
          }
        }
      };
    },
    /**
     * Called by <Form> on every render. The first call seeds the values
     * (existing values win); later calls only update what resetFields goes back to.
     */
    _init(nextInitial) {
      if (nextInitial) initialValues = { ...initialValues, ...nextInitial };
      if (initialized) return false;
      initialized = true;
      values = { ...initialValues, ...values };
      return Object.keys(initialValues).length > 0;
    },
    _setInitialValue(name, value) {
      const path = toPath(name);
      if (getIn(values, path) === undefined) {
        values = setIn(values, path, value);
        initialValues = setIn(initialValues, path, value);
        return true;
      }
      return false;
    },
    _callbacks: callbacks,
    _notify: notify,
    _idPrefix: `f${Math.random().toString(36).slice(2, 8)}`,
  };

  return store;
}

const fieldDomId = (form, name) => `${form._idPrefix}-${toKey(name).replace(/[^\w-]/g, '_')}`;

const FormContext = createContext(null);

function useStoreVersion(form) {
  return useSyncExternalStore(form.subscribe, form.getVersion, form.getVersion);
}

/**
 * Creates a form store and re-renders the calling component when values or errors change.
 * Pass `existing` to share a store created elsewhere.
 */
export function useForm(existing) {
  const ref = useRef(null);
  if (!ref.current) ref.current = existing || createFormStore();
  useStoreVersion(ref.current);
  return ref.current;
}

/** The form store from the nearest <Form>. */
export function useFormInstance() {
  return useContext(FormContext);
}

/** <form> wrapper. Submitting validates every Field, then calls onFinish(values). */
export function Form({
  form: formProp,
  initialValues,
  onFinish,
  onFinishFailed,
  onValuesChange,
  layout,
  className,
  style,
  id,
  autoComplete,
  children,
}) {
  const form = useForm(formProp);
  form._callbacks.onFinish = onFinish;
  form._callbacks.onFinishFailed = onFinishFailed;
  form._callbacks.onValuesChange = onValuesChange;

  const seeded = useRef(false);
  if (form._init(initialValues)) seeded.current = true;
  // Components that read form values before <Form> rendered need one more render.
  useLayoutEffect(() => {
    if (seeded.current) form._notify();
  }, [form]);

  return (
    <FormContext.Provider value={form}>
      <form
        id={id}
        className={cx('form', layout === 'inline' && 'form-inline', className)}
        style={style}
        autoComplete={autoComplete}
        noValidate
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          form.submit();
        }}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
}

function readEvent(eventOrValue, valuePropName) {
  const target = eventOrValue?.target;
  if (!target || typeof target !== 'object' || !('value' in target || 'checked' in target)) {
    return eventOrValue;
  }
  if (valuePropName === 'checked' || target.type === 'checkbox') return target.checked;
  if (target.type === 'number') return target.value === '' ? null : Number(target.value);
  if (target.multiple && target.selectedOptions) {
    return [...target.selectedOptions].map(o => o.value);
  }
  if (target.type === 'file') return target.files;
  return target.value;
}

/**
 * One labelled form control: label, input, and its validation error.
 *
 * children: a single element (value/onChange/id are injected), or a function
 *   field => element, where field = { id, name, value|checked, onChange, aria-invalid }.
 *   Use the function form when the input is nested (e.g. inside .input-affix).
 * valuePropName: 'value' (default) or 'checked' for checkboxes and switches.
 * getValueFromEvent(...args) picks the value from the change event; normalize(value, prev, all) adjusts it.
 */
export function Field({
  name,
  label,
  rules,
  children,
  valuePropName = 'value',
  getValueFromEvent,
  normalize,
  initialValue,
  help,
  extra,
  tooltip,
  required,
  noStyle = false,
  hidden = false,
  className,
  style,
  form: formProp,
}) {
  const contextForm = useContext(FormContext);
  const form = formProp || contextForm;
  if (!form) throw new Error('<Field> must be inside <Form> or get a `form` prop.');
  useStoreVersion(form);

  const fallbackId = useId();
  const meta = useRef({ rules, label });
  meta.current = { rules, label };

  const hasName = name !== undefined && name !== null && name !== '';
  const nameKey = hasName ? toKey(name) : null;

  if (hasName && initialValue !== undefined) form._setInitialValue(name, initialValue);

  useEffect(() => {
    if (!hasName) return undefined;
    return form._register(name, meta);
    // nameKey stands in for `name`, which may be a new array every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, nameKey, hasName]);

  const id = hasName ? fieldDomId(form, name) : fallbackId;
  const error = hasName ? form.getFieldError(name) : undefined;
  const value = hasName ? form.getFieldValue(name) : undefined;
  const isRequired = required ?? (Array.isArray(rules) && rules.some(r => r && typeof r === 'object' && r.required));

  const handleChange = (...args) => {
    let next = getValueFromEvent ? getValueFromEvent(...args) : readEvent(args[0], valuePropName);
    if (normalize) next = normalize(next, value, form.values);
    form._change(name, next);
  };

  const control = hasName
    ? {
        id,
        name: nameKey,
        [valuePropName]: valuePropName === 'checked' ? !!value : value ?? '',
        onChange: handleChange,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? `${id}-error` : undefined,
      }
    : { id };

  let content = children;
  if (typeof children === 'function') {
    content = children(control, { value, error, form });
  } else if (hasName && isValidElement(children)) {
    content = cloneElement(children, {
      ...control,
      onChange: (...args) => {
        handleChange(...args);
        children.props.onChange?.(...args);
      },
    });
  }

  if (noStyle) return content;

  return (
    <div className={cx('form-item', error && 'has-error', className)} style={style} hidden={hidden}>
      {label && (
        <label className={cx('form-label', isRequired && 'is-required')} htmlFor={id}>
          {label}
          {tooltip && (
            <span className="form-label-tip" data-tooltip={typeof tooltip === 'string' ? tooltip : undefined}>
              <AiOutlineQuestionCircle />
            </span>
          )}
        </label>
      )}
      {content}
      {error ? (
        <div className="form-error" id={`${id}-error`} role="alert">
          {error}
        </div>
      ) : (
        help && <div className="form-help">{help}</div>
      )}
      {extra && <div className="form-help">{extra}</div>}
    </div>
  );
}
